/**
 * Centralized error formatter — turns an axios error or any object
 * into a human-readable string. Prevents the "object as a React child"
 * crashes from FastAPI 422 validation arrays and surfaces server-side
 * detail messages so demos/debugging aren't blind.
 *
 * Use everywhere instead of bare `toast.error("Failed to ...")`:
 *   import { formatApiError } from "../lib/errors";
 *   catch (err) { toast.error(formatApiError(err, "Default message")); }
 */
export function formatApiErrorDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (e == null) return "";
        if (typeof e === "string") return e;
        if (typeof e.msg === "string") return e.msg;
        try {
          return JSON.stringify(e);
        } catch {
          return String(e);
        }
      })
      .filter(Boolean)
      .join(" · ");
  }
  if (typeof detail === "object") {
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.message === "string") return detail.message;
    try {
      return JSON.stringify(detail);
    } catch {
      return String(detail);
    }
  }
  return String(detail);
}

export function formatApiError(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  const detail = err?.response?.data?.detail;
  const formatted = formatApiErrorDetail(detail);
  if (formatted) return formatted;
  if (err?.response?.status === 0 || err?.code === "ERR_NETWORK") {
    return "Network error — check your connection and try again.";
  }
  if (err?.response?.status === 429) {
    return "Rate limit reached. Please wait a moment before retrying.";
  }
  if (err?.response?.status >= 500) {
    return "Server error. The team has been notified.";
  }
  if (typeof err?.message === "string") return err.message;
  return fallback;
}
